import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaChartBar, FaLightbulb, FaRocket, FaUsers } from 'react-icons/fa';
import Section from '../../components/common/Section';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['3xl']} 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  display: inline-block;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.space.lg};
  line-height: 1.7;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const FeaturesSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.grey[100]};
  padding: ${({ theme }) => theme.space['2xl']} 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const FeaturesTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.lg};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.dark};
  flex-shrink: 0;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const FeatureDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.6;
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.gradient.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.05;
    pointer-events: none;
  }
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  line-height: 1.7;
`;

const BusinessIntelligence: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Section>
        <HeroSection>
          <HeroTitle>Business <span>Intelligence</span> Solutions</HeroTitle>
          <HeroDescription>
            Transform your data into strategic insights with our comprehensive business intelligence solutions. Make informed decisions faster and drive sustainable growth.
          </HeroDescription>
          <Button variant="accent" size="large">Explore BI Solutions</Button>
        </HeroSection>
        
        <ServicesGrid>
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartBar />
            </IconWrapper>
            <ServiceTitle>Data Visualization</ServiceTitle>
            <ServiceDescription>
              Turn complex data into clear, interactive visualizations that make it easy to identify trends, patterns, and opportunities. Our customized dashboards provide real-time insights at a glance.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaLightbulb />
            </IconWrapper>
            <ServiceTitle>Performance Analytics</ServiceTitle>
            <ServiceDescription>
              Track and analyze key performance indicators across your organization. Our performance analytics solutions help you identify strengths, weaknesses, and opportunities for improvement.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaRocket />
            </IconWrapper>
            <ServiceTitle>Strategic Planning</ServiceTitle>
            <ServiceDescription>
              Use data-driven insights to inform your strategic planning process. Our BI solutions help you set realistic goals, allocate resources effectively, and track progress toward objectives.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Competitive Analysis</ServiceTitle>
            <ServiceDescription>
              Gain a deeper understanding of your market position with our competitive analysis tools. Compare your performance against industry benchmarks and identify opportunities to differentiate.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
        </ServicesGrid>
        
        <FeaturesSection>
          <FeaturesTitle>Key Features of Our BI Solutions</FeaturesTitle>
          <FeaturesList>
            <FeatureItem>
              <FeatureIconWrapper>
                <FaChartBar />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Interactive Dashboards</FeatureTitle>
                <FeatureDescription>
                  Customizable dashboards that provide real-time insights and allow you to explore data from multiple angles with just a few clicks.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIconWrapper>
                <FaLightbulb />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Automated Reporting</FeatureTitle>
                <FeatureDescription>
                  Schedule and automate reports to be delivered to key stakeholders, ensuring everyone has access to the latest insights without manual effort.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIconWrapper>
                <FaRocket />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Advanced Analytics</FeatureTitle>
                <FeatureDescription>
                  Go beyond basic reporting with advanced analytics capabilities, including predictive modeling, trend analysis, and anomaly detection.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIconWrapper>
                <FaUsers />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Collaborative Tools</FeatureTitle>
                <FeatureDescription>
                  Share insights across your organization with collaborative tools that allow team members to comment, annotate, and work together on data analysis.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIconWrapper>
                <FaChartBar />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Mobile Access</FeatureTitle>
                <FeatureDescription>
                  Access your business intelligence tools from anywhere with mobile-optimized dashboards and reports that look great on any device.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIconWrapper>
                <FaLightbulb />
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>Data Integration</FeatureTitle>
                <FeatureDescription>
                  Connect to multiple data sources and integrate information from across your organization for a comprehensive view of your business.
                </FeatureDescription>
              </FeatureContent>
            </FeatureItem>
          </FeaturesList>
        </FeaturesSection>
        
        <CTASection>
          <CTATitle>Ready to Transform Your Decision-Making Process?</CTATitle>
          <CTADescription>
            Let our team of business intelligence experts help you harness the power of your data. Schedule a consultation today to learn how our BI solutions can drive growth and efficiency for your business.
          </CTADescription>
          <Button variant="accent" size="large">Get Started</Button>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default BusinessIntelligence;
